import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { type Observable, of } from 'rxjs';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  SKILLS_IDEATE_PROGRESS_TRACKER,
  SkillsIdeateDataFacade,
  SkillsIdeateEvent,
} from '@core/tellent-skills-logic';
import type { SkillsIdeateResultJson } from '@core/tellent-skills-types';
import {
  DialogBodyComponent,
  DialogComponentI,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  SpinnerComponent,
} from '@core/user-interface-legacy';

import { LoadingGameFrameComponent } from '../../import-skills/import-skills-loading-dialog/loading-game-frame/loading-game-frame.component';

@Component({
  selector: 'tlc-skills-ideate-loading-dialog',
  templateUrl: './skills-ideate-loading-dialog.component.html',
  styleUrl: './skills-ideate-loading-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogBodyComponent,
    DialogHeaderComponent,
    TranslatePipe,
    LoadingGameFrameComponent,
    SpinnerComponent,
  ],
})
export class SkillsIdeateLoadingDialogComponent implements DialogComponentI<
  { id: number },
  { resultJson: SkillsIdeateResultJson } | { error: string }
> {
  private readonly t = injectTranslate();
  private readonly dialogRef = inject(DialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly facade = inject(SkillsIdeateDataFacade);
  private readonly progressTrack = inject(SKILLS_IDEATE_PROGRESS_TRACKER);
  private readonly dialogService = inject(DialogService);

  protected readonly gameStartText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.start',
  );
  protected readonly gameTutorialText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.tutorial',
  );
  protected readonly gameOverText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.game_over',
  );
  protected readonly gameScoreText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.score',
  );

  private isFinishing = false;

  public setDialogData({ id }: { id: number }, _triggerUpdate: boolean): void {
    this.dialogRef.containerInstance.dialogConfig = {
      ...this.dialogRef.containerInstance.dialogConfig,
      closeGuard: () => (this.isFinishing ? of(true) : this.confirmLeave()),
    };
    this.progressTrack
      .track(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: message => {
          if (message.event === SkillsIdeateEvent.Done) {
            this.loadResult(id);
            return;
          }
          if (message.event === SkillsIdeateEvent.Failed) {
            this.finishAndClose({ error: message.reason || this.genericError() });
          }
        },
        error: () => {
          this.finishAndClose({ error: this.genericError() });
        },
      });
  }

  private loadResult(id: number): void {
    this.facade
      .fetchIdeate(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ideate => {
          if (ideate.status === 'failed' || !ideate.resultJson) {
            this.finishAndClose({ error: ideate.errorMessage ?? this.genericError() });
            return;
          }
          this.finishAndClose({ resultJson: ideate.resultJson });
        },
        error: () => {
          this.finishAndClose({ error: this.genericError() });
        },
      });
  }

  private finishAndClose(result: { resultJson: SkillsIdeateResultJson } | { error: string }): void {
    this.isFinishing = true;
    this.dialogRef.close(result);
  }

  private confirmLeave(): Observable<boolean> {
    return this.dialogService.error({
      title: this.t('tlc.positions.skills_ideate_flow.leave_confirmation.loading.title'),
      message: this.t('tlc.positions.skills_ideate_flow.leave_confirmation.loading.message'),
      agreeLabel: this.t('tlc.positions.skills_ideate_flow.leave_confirmation.confirm'),
      isCancelable: true,
    });
  }

  private genericError(): string {
    return this.t('tlc.positions.skills_ideate_flow.result.failed.description');
  }
}
